import { useState, useEffect } from 'react';
import {
  Gauge,
  CreditCard,
  AlertCircle,
  Play,
  Loader2,
  ArrowUpRight,
  BarChart2,
  Archive,
  Gift,
  Share2,
  HelpCircle,
  X,
  Plus,
  Linkedin,
  CheckCircle2,
  BellRing,
  Calendar
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { Link, useLocation } from 'wouter';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AppLayout from '../components/AppLayout';
import MotivationStatsHeader from '../components/MotivationStatsHeader';
import CoachProfilePopover from '../components/CoachProfilePopover';
import { getAllCoachProfiles, getCoachProfile } from '../lib/coachProfiles';
import { getCoachImage, hasCoachImage } from '../lib/coachImages';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { useUsageSummary } from '../hooks/useUsageSummary';
import { useUpdateUserGoal } from '../hooks/useUpdateUserGoal';
import { normalizeCoachId } from '../lib/coachIdMap';

const HomePage = () => {
  const { user } = useUser();
  const [, navigate] = useLocation();
  const firstName = user?.firstName || 'there';
  const [selectedCoaches, setSelectedCoaches] = useState<string[]>([]);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [isStartingQuick, setIsStartingQuick] = useState(false);
  
  // Limit reached overlay states
  const [showLimitReachedOverlay, setShowLimitReachedOverlay] = useState(false);
  const [limitReachedData, setLimitReachedData] = useState<{
    sessionsUsed: number;
    sessionsPurchased: number;
    currentTier: string;
  } | null>(null);

  // Scheduling states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingDate, setSchedulingDate] = useState('');
  const [schedulingTime, setSchedulingTime] = useState('');

  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  // Current user profile (to get recommended coach)
  const { data: profile } = useCurrentUserProfile();

  // Real usage stats
  const { data: usage } = useUsageSummary();

  // Goal update mutation
  const updateGoalMutation = useUpdateUserGoal();

  // Handle goal updates
  const handleAspirationUpdate = async (newGoal: string) => {
    await updateGoalMutation.mutateAsync(newGoal);
  };


  const coachProfiles = getAllCoachProfiles();

  const primaryCoachId =
    normalizeCoachId((profile?.preferred_coach as string) || (profile?.recommended_coach as string)) || 'samuel';
  const empathEarId = 'arjun';
  const primaryCoach = getCoachProfile(primaryCoachId);
  const empathEar = getCoachProfile(empathEarId);

  // Note: Stress-based visual emphasis handled in session defaults, not needed on Home badges here

  // User aspiration from onboarding
  const userAspiration = profile?.goal_statement || "Set your coaching goals in onboarding";

  // Real usage stats (from backend)
  const usageStats = {
    totalSessions: usage?.totalSessions ?? 0,
    currentStreak: usage?.currentStreak ?? 0,
    minutesCoached: usage?.totalMinutes ?? 0,
    lastSessionDate: "Latest session results"
  };

  // Recommended combos based on coaches
  const recommendedCombos = [
    ['samuel', 'arjun'],  // Performance + EmpathEAR
    ['rajesh', 'priya'],  // Leadership + Career
    ['meera', 'aisha']    // Transition + Life
  ];

  const handleQuickStart = async (combo: string[]) => {
    setIsStartingQuick(true);

    try {
      // Check if user has reached session limit
      if (usage && usage.sessionsUsed >= usage.sessionLimit) {
        setLimitReachedData({
          sessionsUsed: usage.sessionsUsed,
          sessionsPurchased: usage.sessionLimit,
          currentTier: usage.currentTier || 'trial',
        });
        setShowLimitReachedOverlay(true);
        setIsStartingQuick(false);
        return;
      }

      // Create URL with selected coaches as parameters
      const coachParams = combo.join(',');

      // Add smooth transition delay for Apple-inspired UX
      await new Promise(resolve => setTimeout(resolve, 800));

      // Navigate to coaching workspace with selected coaches
      navigate(`/coaching/session?coaches=${encodeURIComponent(coachParams)}`);
    } catch (error) {
      handleSessionLimitReached(error);
      setIsStartingQuick(false);
    }
  };

  const handleCoachSelect = (coachId: string) => {
    if (selectedCoaches.includes(coachId)) {
      setSelectedCoaches(selectedCoaches.filter(id => id !== coachId));
    } else if (selectedCoaches.length < 3) {
      setSelectedCoaches([...selectedCoaches, coachId]);
    }
  };

  const handleStartThinkTank = async () => {
    if (selectedCoaches.length > 0) {
      setIsStartingSession(true);

      try {
        // Check if user has reached session limit
        if (usage && usage.sessionsUsed >= usage.sessionLimit) {
          setLimitReachedData({
            sessionsUsed: usage.sessionsUsed,
            sessionsPurchased: usage.sessionLimit,
            currentTier: usage.currentTier || 'trial',
          });
          setShowLimitReachedOverlay(true);
          setIsStartingSession(false);
          return;
        }

        // Create URL with selected coaches as parameters
        const coachParams = selectedCoaches.join(',');

        // Add smooth transition delay for Apple-inspired UX
        await new Promise(resolve => setTimeout(resolve, 800));

        // Navigate to coaching workspace with selected coaches
        navigate(`/coaching/session?coaches=${encodeURIComponent(coachParams)}`);
      } catch (error) {
        handleSessionLimitReached(error);
        setIsStartingSession(false);
      }
    }
  };

  const primaryQuickCombo = recommendedCombos[0] ?? ['samuel', 'arjun'];

  // Subscription zone helpers
  const getSubscriptionZone = (usage: any) => {
    if (!usage) return 'green';
    const percentage = (usage.sessionsUsed / usage.sessionLimit) * 100;
    if (percentage <= 42) return 'green';  // 0-3 sessions of 7
    if (percentage <= 85) return 'yellow'; // 4-6 sessions of 7  
    return 'red'; // 7+ sessions
  };

  const getPlanDisplayName = (usage: any) => {
    if (!usage) return 'Free Trial';
    if (usage.currentTier === 'trial') return 'Free Trial';
    if (usage.currentTier === 'basic') return 'Basic Plan';
    if (usage.currentTier === 'growth') return 'Growth Plan';
    if (usage.currentTier === 'executive') return 'Executive Plan';
    return 'Your Plan';
  };

  const subscriptionZone = getSubscriptionZone(usage);
  const planDisplayName = getPlanDisplayName(usage);

  // Enhanced subscription status badge logic
  const getSubscriptionStatusBadge = (usage: any) => {
    if (!usage) return { status: 'Trial', color: 'bg-blue-100 text-blue-700' };
    
    if (usage.currentTier === 'trial') return { status: 'Trial', color: 'bg-blue-100 text-blue-700' };
    if (usage.currentTier === 'cancelled') return { status: 'Can.', color: 'bg-red-100 text-red-700' };
    if (['basic', 'growth', 'executive'].includes(usage.currentTier)) {
      return { status: 'Sub.', color: 'bg-green-100 text-green-700' };
    }
    return { status: 'Trial', color: 'bg-blue-100 text-blue-700' };
  };

  const statusBadge = getSubscriptionStatusBadge(usage);

  // Progress width helper function (same as DashboardPage)
  const progressWidthClass = (used: number, purchased: number) => {
    if (!purchased) return 'w-0';
    const pct = Math.min(Math.round((used / purchased) * 100), 100);
    const step = Math.round(pct / 10) * 10;
    return `w-[${step}%]`;
  };

  // Handle session limit reached (same logic as DashboardPage)
  const handleSessionLimitReached = (error: any) => {
    try {
      const errorData = JSON.parse(error.message);
      if (errorData.sessionUsage) {
        setLimitReachedData({
          sessionsUsed: errorData.sessionUsage.used,
          sessionsPurchased: errorData.sessionUsage.purchased,
          currentTier: errorData.sessionUsage.tier,
        });
        setShowLimitReachedOverlay(true);
        return;
      }
    } catch (parseError) {
      // If parsing fails, check for session limit keywords
      if (error.message.includes('coaching sessions') || error.message.includes('session limit')) {
        setLimitReachedData({
          sessionsUsed: usage?.sessionsUsed || 0,
          sessionsPurchased: usage?.sessionLimit || 7,
          currentTier: usage?.currentTier || 'trial',
        });
        setShowLimitReachedOverlay(true);
        return;
      }
    }
  };

  const updateNudgeMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = await getToken();
      if (!token) throw new Error('Auth required');
      const { updateNudgeSettings } = await import('../lib/api');
      return updateNudgeSettings(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({ queryKey: ['users-me'] });
    }
  });

  const handleScheduleSession = async () => {
    if (!schedulingDate || !schedulingTime) return;
    
    try {
      const fullDateTime = new Date(`${schedulingDate}T${schedulingTime}`);
      
      if (fullDateTime < new Date()) {
        alert('Please select a future date and time.');
        return;
      }
      
      await updateNudgeMutation.mutateAsync({
        nextSessionAt: fullDateTime.toISOString()
      });

      setShowScheduleModal(false);
      setSchedulingDate('');
      setSchedulingTime('');
    } catch (error) {
      console.error('Failed to schedule session:', error);
    }
  };

  return (
    <AppLayout currentPage="home">
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8">
        {/* Above the Fold Content */}
        <div className="max-w-7xl mx-auto space-y-12">

          {/* Welcome Header */}
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm border border-royal-blue/20">
                <img
                  src="/hipo-logo.png"
                  alt="HiPo AI Coach"
                  className="h-8 w-8 object-contain"
                />
              </div>
              <div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                  Welcome back, {firstName}
                </h1>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  Your personalized coaching dashboard
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Clickable Session Counter with Status Badge */}
              <Link href="/payments" className="inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-display font-semibold shadow-sm transition-all hover:shadow-md cursor-pointer border border-gray-200 bg-white hover:bg-gray-50" title="Includes up to 21 carry-forward sessions">
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusBadge.color}`}>
                  {statusBadge.status}
                </span>
                <span className="text-gray-700">{usage?.sessionsUsed || 0}/{usage?.sessionLimit || 7} Sessions</span>
                <ArrowUpRight className="h-4 w-4 text-gray-500" />
              </Link>

              {localStorage.getItem('hipo_last_coaching_session_id') && (
                <button
                  onClick={() => navigate(`/coaching/session?session=${localStorage.getItem('hipo_last_coaching_session_id')}`)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-royal-blue px-5 py-2.5 font-display text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-royal-blue/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-blue focus-visible:ring-offset-2 min-h-[44px]"
                >
                  <ArrowUpRight className="h-4 w-4 text-white" strokeWidth={1.5} />
                  <span className="text-white">Resume Active Session</span>
                </button>
              )}
            </div>
          </div>

          {/* Notice Boards */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 flex items-center gap-6">
              <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Sessions Completed</p>
                <h3 className="text-3xl font-bold text-slate-900">{profile?.sessions_completed ?? 0}</h3>
                <p className="text-xs text-slate-400 mt-1">Keep it up! Consistency is key.</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 flex items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="h-14 w-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <BellRing className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Next Coaching Session</p>
                  <h3 className="text-xl font-bold text-slate-900">
                    {profile?.next_session_at && new Date(profile.next_session_at) > new Date()
                      ? new Date(profile.next_session_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                      : profile?.last_session_at 
                        ? new Date(new Date(profile.last_session_at).getTime() + 7 * 24 * 3600 * 1000).toLocaleDateString([], { dateStyle: 'medium' })
                        : "Not scheduled"}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    {profile?.next_session_at && new Date(profile.next_session_at) > new Date()
                      ? "Your next session is set." 
                      : profile?.last_session_at 
                        ? "Default schedule (7 days after last session)" 
                        : "Stay on track with regular sessions."}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowScheduleModal(true)}
                className="px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold transition-colors border border-slate-200"
              >
                Schedule
              </button>
            </div>
          </section>
        
        </div>
        
        {/* FULL-WIDTH ASPIRATIONAL SECTION - Outside container */}
        <div className="w-full bg-gradient-to-r from-blue-50 via-white to-indigo-50 py-4 my-6 border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-6">
              <h2 className="font-display text-lg font-medium text-royal-blue italic leading-relaxed">
                "First you aspire, then you soar"
              </h2>
              <div className="max-w-3xl mx-auto">
                <MotivationStatsHeader
                  userAspiration={userAspiration}
                  totalSessions={0}
                  currentStreak={0}
                  minutesCoached={0}
                  lastSessionDate=""
                  onAspirationUpdate={handleAspirationUpdate}
                  isUpdatingAspiration={updateGoalMutation.isPending}
                  hideStats={true}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          {/* PRIMARY FOCUS: Clean Coach Selection */}
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight mb-2">
                Meet Your Coaches
              </h2>
              <p className="text-slate-600 leading-relaxed max-w-2xl mx-auto">
                Select the coach that best fits your current need.<br />
                You can also combine multiple coach personas for broader perspective.<br />
                For best results, begin with one coach and expand as needed.
              </p>
            </div>

            {/* High-Density Coach Grid: 3x2 on desktop, carousel on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {coachProfiles.map((coach: any) => {
                const isSelected = selectedCoaches.includes(coach.id);
                const isDisabled = !isSelected && selectedCoaches.length >= 3;
                const coachProfile = getCoachProfile(coach.id);

                return (
                  <CoachProfilePopover key={coach.id} coach={coach}>
                    <button
                      onClick={() => handleCoachSelect(coach.id)}
                      disabled={isDisabled}
                      className={`group relative w-full rounded-3xl p-6 sm:p-8 transition-all duration-300 ${isSelected
                        ? 'bg-royal-blue border-2 border-royal-blue shadow-lg scale-105'
                        : isDisabled
                          ? 'bg-white/60 border border-alto-grey opacity-50 cursor-not-allowed'
                          : 'bg-white border border-alto-grey hover:bg-blue-50/50 hover:shadow-lg hover:scale-102'
                        }`}
                    >
                      {/* Selection Indicator */}
                      {isSelected && (
                        <div className="absolute top-3 left-3 sm:top-4 sm:left-4 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm z-10">
                          <span className="text-xs font-display font-bold text-royal-blue">
                            {selectedCoaches.indexOf(coach.id) + 1}
                          </span>
                        </div>
                      )}

                      {/* Core Team Badges on Cards */}
                      {coach.id === primaryCoachId && (
                        <span className={`absolute top-4 right-4 rounded-full px-3 py-1 text-xs font-display font-semibold shadow-sm ${isSelected ? '!bg-white !text-royal-blue' : 'bg-royal-blue/90 !text-white'}`}>
                          Primary Coach
                        </span>
                      )}
                      {coach.id === empathEarId && (
                        <span className={`absolute top-4 right-4 rounded-full px-3 py-1 text-xs font-display font-semibold shadow-sm ${isSelected ? '!bg-white !text-royal-blue' : 'bg-pink-100 text-pink-700'}`}>
                          Secondary Coach
                        </span>
                      )}

                      {/* Coach Avatar */}
                      <div className="mx-auto mb-6 h-24 w-24 rounded-full overflow-hidden shadow-md bg-white border-2 border-gray-100">
                        {hasCoachImage(coach.id) ? (
                          <img
                            src={getCoachImage(coach.id)}
                            alt={`${coach.name} - Coach Avatar`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className={`w-full h-full bg-gradient-to-br ${coach.color} flex items-center justify-center text-white`}>
                            <span className="text-2xl font-display font-bold">
                              {coach.name.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Coach Info */}
                      <div className="text-center space-y-2">
                        <h3 className={`font-display text-xl font-bold tracking-tight ${isSelected ? '!text-white' : 'text-slate-900'}`}>
                          {coach.name}
                        </h3>
                        <p className={`text-sm font-display font-medium uppercase tracking-wider ${isSelected ? '!text-white' : 'text-[#4C62E3]'}`}>
                          {coach.expertise}
                        </p>

                        {/* Top Specialties */}
                        <div className="flex flex-wrap gap-1 justify-center mt-3">
                          {coach.specialties.slice(0, 2).map((specialty: string) => (
                            <span
                              key={specialty}
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-display font-medium capitalize ${isSelected ? '!bg-white !text-royal-blue' : 'bg-gray-100 text-gray-700'}`}
                            >
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                    </button>
                  </CoachProfilePopover>
                );
              })}
            </div>

            {/* Start Session Button */}
            {selectedCoaches.length > 0 && (
              <div className="text-center pt-6">
                <button
                  onClick={handleStartThinkTank}
                  disabled={selectedCoaches.length === 0 || isStartingSession}
                  className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-display font-bold shadow-sm transition-all duration-200 min-h-[56px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${selectedCoaches.length > 0 && !isStartingSession
                    ? 'bg-[#EE7D79] text-white hover:bg-[#E56B66] focus-visible:ring-[#EE7D79]'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  {isStartingSession ? (
                    <Loader2 className="h-5 w-5 animate-spin text-white" strokeWidth={1.5} />
                  ) : (
                    <Play className="h-5 w-5 text-white" strokeWidth={1.5} />
                  )}
                  <span className='text-white'>{isStartingSession ? 'Starting...' : 'Start Coaching Session'}</span>
                  {selectedCoaches.length > 0 && !isStartingSession && (
                    <span className="ml-1 text-white">({selectedCoaches.length} coach{selectedCoaches.length !== 1 ? 'es' : ''})</span>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* SECONDARY FOCUS: Think Tank Quick Start */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-3xl p-8 border border-[#D5D5D5] mt-6">
            <div className="text-center mb-6">
              <h3 className="font-display text-lg font-semibold text-slate-900 tracking-tight mb-2">
                Choose your personalised coach combination
              </h3>
              <p className="font-body text-sm text-slate-600 leading-relaxed">
                Or try these curated coach combinations
              </p>
            </div>

            {/* Compact Coach Combo Cards */}
            <div className="grid gap-4 sm:grid-cols-3">
              {recommendedCombos.map((combo, index) => {
                const comboProfiles = combo.map(id => getCoachProfile(id)).filter(Boolean);

                return (
                  <button
                    key={combo.join('-')}
                    onClick={() => handleQuickStart(combo)}
                    disabled={isStartingQuick}
                    className={`group relative overflow-hidden rounded-2xl p-4 shadow-sm transition-all duration-300 text-left border border-[#D5D5D5]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C62E3] focus-visible:ring-offset-2 ${isStartingQuick ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:shadow-md'
                      }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center -space-x-2">
                        {combo.map((coachId) => {
                          const coachProfile = getCoachProfile(coachId);
                          if (!coachProfile) return null;
                          return (
                            <div
                              key={coachId}
                              className="relative h-8 w-8 overflow-hidden rounded-full border-2 border-white shadow-sm bg-white"
                            >
                              {hasCoachImage(coachId) ? (
                                <img
                                  src={getCoachImage(coachId)}
                                  alt={coachProfile.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className={`w-full h-full bg-gradient-to-br ${coachProfile.color} flex items-center justify-center text-white font-semibold text-xs`}>
                                  {coachProfile.name.charAt(0)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display text-sm font-semibold text-slate-900 truncate">
                          {comboProfiles.map(p => p?.name).join(' + ')}
                        </h4>
                        <p className="text-xs text-slate-600">
                          {index === 0 ? 'Performance Coaching with Listening Ear' : index === 1 ? 'Career Coaching for Leaders' : 'Transition whole life coaching'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs font-display font-medium text-slate-500">
                        Start
                      </span>
                      <ArrowUpRight className="h-3 w-3 text-slate-400 group-hover:text-royal-blue transition-colors" strokeWidth={1.5} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* LinkedIn Community Link */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-center">
              <a
                href="https://www.linkedin.com/company/hipo-career-solutions-llp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-[#0077B5] text-white font-semibold text-sm hover:bg-[#005885] transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Linkedin className="h-5 w-5" />
                Join the HiPo Community on LinkedIn
              </a>
            </div>
            <p className="text-center text-xs text-gray-500 mt-4">
              Join our professional network for career insights and exclusive updates
            </p>
          </div>
        </div>
      </div>


      {/* Limit Reached Overlay */}
      {showLimitReachedOverlay && limitReachedData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Session Limit Reached</h3>
              </div>
              <button
                onClick={() => setShowLimitReachedOverlay(false)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close overlay"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Sessions Used</span>
                  <span className="text-sm font-bold text-gray-900">
                    {limitReachedData.sessionsUsed} / {limitReachedData.sessionsPurchased}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`bg-amber-500 h-2 rounded-full ${progressWidthClass(limitReachedData.sessionsUsed, limitReachedData.sessionsPurchased)}`}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Current tier: {limitReachedData.currentTier}</p>
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  You've used all your coaching sessions for this billing period. Upgrade to continue your growth journey.
                </p>
                
                <div className="space-y-3">
                  <Link href="/payments">
                    <button 
                      className="w-full flex items-center justify-center gap-2 bg-royal-blue text-white px-4 py-3 rounded-lg font-medium hover:bg-royal-blue/90 transition"
                      onClick={() => setShowLimitReachedOverlay(false)}
                    >
                      <CreditCard className="h-4 w-4" />
                      Upgrade Plan
                    </button>
                  </Link>
                  
                  <Link href="/payments?intent=topup&from=overlay">
                    <button 
                      className="w-full flex items-center justify-center gap-2 border border-gray-300 bg-white px-4 py-3 rounded-lg font-medium hover:bg-gray-50 transition"
                      onClick={() => setShowLimitReachedOverlay(false)}
                    >
                      <Plus className="h-4 w-4" />
                      Get 3 More Sessions (₹40)
                    </button>
                  </Link>
                  
                  <button 
                    className="w-full text-gray-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition"
                    onClick={() => setShowLimitReachedOverlay(false)}
                  >
                    Maybe Later
                  </button>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center mb-2">
                    Having issues or feedback about our pricing?
                  </p>
                  <Link href="/feedback">
                    <button 
                      className="w-full text-royal-blue text-sm font-medium hover:underline"
                      onClick={() => setShowLimitReachedOverlay(false)}
                    >
                      Share Your Feedback
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100">
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Schedule Session</h2>
                </div>
                <button 
                  onClick={() => setShowScheduleModal(false)}
                  className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Select Date</label>
                  <input 
                    type="date" 
                    value={schedulingDate}
                    onChange={(e) => setSchedulingDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Select Time</label>
                  <input 
                    type="time" 
                    value={schedulingTime}
                    onChange={(e) => setSchedulingTime(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-3">
                <button 
                  onClick={handleScheduleSession}
                  disabled={!schedulingDate || !schedulingTime || updateNudgeMutation.isPending}
                  className="w-full py-4 bg-royal-blue text-white rounded-2xl font-bold text-lg shadow-lg shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-3"
                >
                  {updateNudgeMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Confirm Schedule'
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  We'll send you an email nudge before your session.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default HomePage;
